import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import WeatherIcon from "../components/WeatherIcon";
import LocalTimeCard from "../components/LocalTimeCard";
import { DashboardLayout } from "../components/dashboard/DashboardLayout";
import { HuntingWidget } from "../components/dashboard/HuntingWidget";
import { useDashboardLocationSearch } from "../hooks/useDashboardLocationSearch";
import { getMoonTimes } from "../utils/moon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function Dashboard() {
  const { user, logout, updatePreferences, token } = useAuth();
  const navigate = useNavigate();
  const { theme, toggleTheme, updateSunTimes } = useTheme();
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [extendedForecast, setExtendedForecast] = useState(null);
  const [minutelyRain, setMinutelyRain] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [severeAlerts, setSevereAlerts] = useState([]);
  const [airQuality, setAirQuality] = useState(null);
  const [uvIndex, setUvIndex] = useState(null);
  const [aiSummary, setAiSummary] = useState("");
  const [loadingAi, setLoadingAi] = useState(false);
  const [loading, setLoading] = useState(true);
  const clearAiSummary = useCallback(() => setAiSummary(""), []);
  
  // Subscription & Premium state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [discountEndDate] = useState(new Date("2026-05-01"));
  const [showDashboardCustomizer, setShowDashboardCustomizer] = useState(false);
  const [dashboardWidgets, setDashboardWidgets] = useState(null);
  
  const isDarkMode = theme === "dark";
  
  // Check if user has premium access
  const isPremium = subscriptionStatus?.is_premium || subscriptionStatus?.is_trial || user?.role === "admin";
  const isTrial = subscriptionStatus?.is_trial;
  const trialDaysLeft = subscriptionStatus?.subscription?.trial_ends 
    ? Math.ceil((new Date(subscriptionStatus.subscription.trial_ends) - new Date()) / (1000 * 60 * 60 * 24))
    : 0;
  
  // Helper function to check if a widget/section is visible
  const isWidgetVisible = (widgetId) => {
    if (!dashboardWidgets) return true; // Show all by default
    const widget = dashboardWidgets.find(w => w.id === widgetId);
    return widget ? widget.enabled : true;
  };
  
  // Helper function to get widget order (for reorderable widgets)
  const getWidgetOrder = (widgetId) => {
    if (!dashboardWidgets) return 999;
    const widget = dashboardWidgets.find(w => w.id === widgetId);
    return widget?.order ?? 999;
  };
  
  // Define reorderable widgets (Additional Widgets section)
  const REORDERABLE_WIDGETS = [
    'hourly', 'extended', 'minutely', 'health', 'flight', 'storm',
    'historical', 'astronomy', 'marine', 'lake-river', 'fishing', 'hunting', 'ski', 'flight', 'sports-field', 'event-weather', 'gardening', 'alerts',
    'visualizations', 'pws', 'radar'
  ];
  
  // Get sorted reorderable widgets
  const getSortedReorderableWidgets = () => {
    return REORDERABLE_WIDGETS
      .filter(id => isWidgetVisible(id))
      .sort((a, b) => getWidgetOrder(a) - getWidgetOrder(b));
  };
  
  // Temperature unit from user preferences
  const tempUnit = user?.preferences?.temp_unit || "fahrenheit";
  const isCelsius = tempUnit === "celsius";
  
  // Convert temperature based on unit
  const convertTemp = (tempF) => {
    if (isCelsius) {
      return Math.round((tempF - 32) * 5 / 9);
    }
    return Math.round(tempF);
  };
  
  // Fetch subscription status
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!token) return;
      try {
        const [statusRes, firstVisitRes, dashboardConfigRes] = await Promise.all([
          axios.get(`${API}/subscription/status`, { headers: { Authorization: `Bearer ${token}` }}),
          axios.get(`${API}/subscription/check-first-visit`, { headers: { Authorization: `Bearer ${token}` }}),
          axios.get(`${API}/user/dashboard-config`, { headers: { Authorization: `Bearer ${token}` }}).catch(e => { console.error("Dashboard config error:", e.message); return { data: null }; })
        ]);
        setSubscriptionStatus(statusRes.data);
        
        // Load saved dashboard configuration
        if (dashboardConfigRes.data?.widgets) {
          setDashboardWidgets(dashboardConfigRes.data.widgets);
        }
        
        if (firstVisitRes.data.show_onboarding) {
          setShowOnboarding(true);
        }
      } catch (err) {
        console.error("Subscription status fetch error:", err.message);
      }
    };
    fetchSubscription();
  }, [token]);

  const tempSymbol = isCelsius ? "°C" : "°F";

  const {
    gettingLocation,
    isLocationSaved,
    isSearching,
    location,
    removeLocation,
    requestGPSLocation,
    saveLocation,
    savedLocations,
    searchQuery,
    searchResults,
    selectLocation,
    setLocation,
    setSearchQuery,
  } = useDashboardLocationSearch({
    user,
    updatePreferences,
    onLocationSelected: clearAiSummary,
  });
  
  // Toggle temperature unit
  const toggleTempUnit = async () => {
    const newUnit = isCelsius ? "fahrenheit" : "celsius";
    try {
      await axios.put(`${API}/user/preferences`, { temp_unit: newUnit });
      if (updatePreferences) {
        updatePreferences({ temp_unit: newUnit });
      }
      toast.success(`Switched to ${newUnit === "celsius" ? "Celsius" : "Fahrenheit"}`);
    } catch (error) {
      toast.error("Failed to update temperature unit");
    }
  };

  const fetchWeather = useCallback(async (lat, lon) => {
    setLoading(true);
    try {
      const [currentRes, forecastRes, aqRes, uvRes, extendedRes, minutelyRes, healthRes, alertsRes] = await Promise.all([
        axios.get(`${API}/weather/current`, { params: { lat, lon } }).catch(e => { console.error("Current weather error:", e.message); return null; }),
        axios.get(`${API}/weather/forecast`, { params: { lat, lon } }).catch(e => { console.error("Forecast error:", e.message); return null; }),
        axios.get(`${API}/weather/airquality`, { params: { lat, lon } }).catch(e => { console.error("Air quality error:", e.message); return null; }),
        axios.get(`${API}/weather/uv`, { params: { lat, lon } }).catch(e => { console.error("UV error:", e.message); return null; }),
        axios.get(`${API}/weather/forecast/extended`, { params: { lat, lon } }).catch(() => null),
        axios.get(`${API}/weather/minutely`, { params: { lat, lon } }).catch(e => { console.error("Minutely error:", e.message); return null; }),
        axios.get(`${API}/weather/health`, { params: { lat, lon } }).catch(e => { console.error("Health error:", e.message); return null; }),
        axios.get(`${API}/weather/alerts`, { params: { lat, lon } }).catch(e => { console.error("Alerts error:", e.message); return { data: { alerts: [] } }; })
      ]);
      
      if (currentRes?.data) setWeather(currentRes.data);
      if (forecastRes?.data) setForecast(forecastRes.data);
      if (aqRes?.data) setAirQuality(aqRes.data);
      if (uvRes?.data) setUvIndex(uvRes.data);
      if (extendedRes?.data) setExtendedForecast(extendedRes.data);
      if (minutelyRes?.data) setMinutelyRain(minutelyRes.data);
      if (healthRes?.data) setHealthData(healthRes.data);
      setSevereAlerts(alertsRes?.data?.alerts || []);
      
      // Update sun times for auto theme mode
      if (currentRes?.data?.sys?.sunrise && currentRes?.data?.sys?.sunset) {
        const timezoneOffset = currentRes.data?.timezone || 0;
        updateSunTimes(currentRes.data.sys.sunrise, currentRes.data.sys.sunset, timezoneOffset);
      }
      
      // Only show error if the critical current weather call failed
      if (!currentRes?.data) {
        toast.error("Weather data temporarily unavailable. Please try refreshing.");
      }
    } catch (error) {
      toast.error("Failed to fetch weather data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [updateSunTimes]);

  // Pull-to-refresh handler
  const handleRefresh = async () => {
    if (location) {
      toast.info("Refreshing weather data...", { id: "refresh", duration: 2000 });
      await fetchWeather(location.lat, location.lon);
      toast.success("Weather updated!", { id: "refresh" });
    }
  };

  useEffect(() => {
    if (location) {
      fetchWeather(location.lat, location.lon);
    }
  }, [location, fetchWeather]);

  const generateAiSummary = async () => {
    if (!forecast) return;
    setLoadingAi(true);
    try {
      const response = await axios.post(`${API}/weather/summary`, {
        weather_data: forecast,
        location_name: location.name
      });
      setAiSummary(response.data.summary);
    } catch (error) {
      toast.error("Failed to generate AI summary");
    } finally {
      setLoadingAi(false);
    }
  };

  const getBackgroundImage = () => "/assets/dashboard-background-symbol.png";

  const formatTime = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit"
    });
  };

  // Loading state while getting location
  if (gettingLocation) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#050505]">
        <Loader2 className="w-12 h-12 animate-spin text-[#00f2ea]" />
        <p className="text-white/60">Getting your location...</p>
      </div>
    );
  }

  // Loading weather data
  if (loading && !weather) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#050505]">
        <Loader2 className="w-12 h-12 animate-spin text-[#00f2ea]" />
        <p className="text-white/60">Loading weather data...</p>
      </div>
    );
  }

  const moonData = weather ? getMoonTimes(weather.sys.sunrise, weather.sys.sunset) : null;

  // Handle start trial from onboarding
  const handleStartTrial = async () => {
    try {
      await axios.post(`${API}/subscription/start-trial`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("🎉 Your 7-day free trial has started!");
      // Refresh subscription status
      const res = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start trial");
    }
  };

  // Complete onboarding
  const completeOnboarding = async () => {
    setShowOnboarding(false);
    try {
      await axios.post(`${API}/subscription/complete-onboarding`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error("Failed to complete onboarding:", err.message);
    }
  };

  return (
    <DashboardLayout
      AccuracyItem={item}
      DashboardContainer={container}
      HuntingWidget={HuntingWidget}
      LocalTimeCard={LocalTimeCard}
      WeatherIcon={WeatherIcon}
      aiSummary={aiSummary}
      completeOnboarding={completeOnboarding}
      convertTemp={convertTemp}
      dashboardWidgets={dashboardWidgets}
      discountEndDate={discountEndDate}
      extendedForecast={extendedForecast}
      fetchWeather={fetchWeather}
      forecast={forecast}
      formatTime={formatTime}
      generateAiSummary={generateAiSummary}
      getBackgroundImage={getBackgroundImage}
      handleRefresh={handleRefresh}
      handleStartTrial={handleStartTrial}
      healthData={healthData}
      isCelsius={isCelsius}
      isDarkMode={isDarkMode}
      isLocationSaved={isLocationSaved}
      isPremium={isPremium}
      isSearching={isSearching}
      isTrial={isTrial}
      isWidgetVisible={isWidgetVisible}
      loading={loading}
      loadingAi={loadingAi}
      location={location}
      logout={logout}
      moonData={moonData}
      removeLocation={removeLocation}
      requestGPSLocation={requestGPSLocation}
      saveLocation={saveLocation}
      savedLocations={savedLocations}
      searchQuery={searchQuery}
      searchResults={searchResults}
      selectLocation={selectLocation}
      severeAlerts={severeAlerts}
      setAiSummary={setAiSummary}
      setDashboardWidgets={setDashboardWidgets}
      setExtendedForecast={setExtendedForecast}
      setForecast={setForecast}
      setLocation={setLocation}
      setSearchQuery={setSearchQuery}
      setShowDashboardCustomizer={setShowDashboardCustomizer}
      setWeather={setWeather}
      showDashboardCustomizer={showDashboardCustomizer}
      showOnboarding={showOnboarding}
      tempSymbol={tempSymbol}
      tempUnit={tempUnit}
      token={token}
      toggleTempUnit={toggleTempUnit}
      toggleTheme={toggleTheme}
      trialDaysLeft={trialDaysLeft}
      user={user}
      weather={weather}
    />
  );
}
